import { z } from 'zod';
import { DIRECTIONS, MODALITY_SPECS, type Direction, type Modality } from '../contracts';
import { accumulatorBot } from './accumulator';
import { comboRobotSchema, comboStrategy, type ComboParams, type ComboState } from './combo';
import { customStrategy, ruleWindow, validateCustomDef } from './custom';
import { autoPresets, DEFAULT_INVEXA_PRESETS, PRESET_LIMITS, presetStrategyFor, type InvexaPreset } from './presets';
import type { AnyStrategy, Strategy } from './types';

export * from './types';
export * from './custom';
export * from './combo';
export * from './backtest';
export * from './scan';
export * from './round-pack';
export * from './analysis';
export * from './accumulator';
export * from './presets';
export * from './frequency';

const directionSchema = z.enum(DIRECTIONS);
const barrierSchema = z.number().int().min(0).max(9).optional();

function defaultDirection(modality: Modality): Direction {
  return MODALITY_SPECS[modality].directions[0].direction;
}

function defaultBarrier(modality: Modality, direction: Direction): number | undefined {
  return MODALITY_SPECS[modality].directions.find((d) => d.direction === direction)?.barrier?.default;
}

function opposite(modality: Modality, direction: Direction): Direction {
  // One-sided modalities (Acumulador) have no opposite: the same contract.
  const [a, b = a] = MODALITY_SPECS[modality].directions;
  return a.direction === direction ? b.direction : a.direction;
}

// ---------------------------------------------------------------------------

type FixedParams = { direction: Direction; barrier?: number | undefined };

export const fixedDirection: Strategy<FixedParams, Record<string, never>> = {
  key: 'fixed_direction',
  name: 'Direção fixa',
  description: 'Entra sempre na mesma direção, uma vez por rodada, dentro dos limites.',
  modalities: ['EVEN_ODD', 'RISE_FALL', 'MATCH_DIFF', 'OVER_UNDER'],
  paramsSchema: z.object({ direction: directionSchema, barrier: barrierSchema }),
  defaultParams: (m) => ({ direction: defaultDirection(m), barrier: defaultBarrier(m, defaultDirection(m)) }),
  initialState: () => ({}),
  onTick: (ctx) => ({
    intent: { direction: ctx.params.direction, barrier: ctx.params.barrier },
    state: ctx.state,
    reason: 'Direção fixa configurada',
  }),
  onSettlement: (ctx) => ctx.state,
};

// ---------------------------------------------------------------------------

type AlternateParams = { startDirection: Direction; barrier?: number | undefined };
type AlternateState = { next: Direction | null };

export const alternating: Strategy<AlternateParams, AlternateState> = {
  key: 'alternating',
  name: 'Alternada',
  description: 'Alterna a direção a cada rodada liquidada, começando pela direção escolhida.',
  modalities: ['EVEN_ODD', 'RISE_FALL', 'MATCH_DIFF', 'OVER_UNDER'],
  paramsSchema: z.object({ startDirection: directionSchema, barrier: barrierSchema }),
  defaultParams: (m) => ({ startDirection: defaultDirection(m), barrier: defaultBarrier(m, defaultDirection(m)) }),
  initialState: (p) => ({ next: p.startDirection }),
  onTick: (ctx) => {
    const direction = ctx.state.next ?? ctx.params.startDirection;
    return { intent: { direction, barrier: ctx.params.barrier }, state: ctx.state, reason: `Alternância: ${direction}` };
  },
  onSettlement: (ctx, settled) => ({ next: opposite(ctx.modality, settled.direction) }),
};

// ---------------------------------------------------------------------------

type SwitchParams = { startDirection: Direction; barrier?: number | undefined };
type SwitchState = { current: Direction | null };

export const switchAfterLoss: Strategy<SwitchParams, SwitchState> = {
  key: 'switch_after_loss',
  name: 'Troca após perda',
  description: 'Mantém a direção após um WIN e troca para a direção oposta após um LOSS.',
  modalities: ['EVEN_ODD', 'RISE_FALL', 'MATCH_DIFF', 'OVER_UNDER'],
  paramsSchema: z.object({ startDirection: directionSchema, barrier: barrierSchema }),
  defaultParams: (m) => ({ startDirection: defaultDirection(m), barrier: defaultBarrier(m, defaultDirection(m)) }),
  initialState: (p) => ({ current: p.startDirection }),
  onTick: (ctx) => {
    const direction = ctx.state.current ?? ctx.params.startDirection;
    return { intent: { direction, barrier: ctx.params.barrier }, state: ctx.state, reason: `Direção atual: ${direction}` };
  },
  onSettlement: (ctx, settled) => ({
    current: settled.outcome === 'LOSS' ? opposite(ctx.modality, settled.direction) : settled.direction,
  }),
};

// ---------------------------------------------------------------------------

type StreakParams = { streakLength: number; entry: 'WITH' | 'AGAINST' };

export const streakTrigger: Strategy<StreakParams, Record<string, never>> = {
  key: 'streak_trigger',
  name: 'Gatilho de sequência',
  description:
    'Aguarda N rodadas seguidas com o mesmo resultado (par/ímpar ou sobe/desce) e entra a favor ou contra. Sequências passadas não alteram a probabilidade do próximo resultado.',
  modalities: ['EVEN_ODD', 'RISE_FALL'],
  paramsSchema: z.object({ streakLength: z.number().int().min(2).max(12), entry: z.enum(['WITH', 'AGAINST']) }),
  defaultParams: () => ({ streakLength: 4, entry: 'AGAINST' }),
  initialState: () => ({}),
  onTick: (ctx) => {
    const n = ctx.params.streakLength;
    let side: Direction | null = null;
    if (ctx.modality === 'EVEN_ODD') {
      // Empates are skipped: the streak goes on across them.
      const recent = ruleWindow(ctx.modality, ctx.window).sides.slice(0, n);
      if (recent.length === n) {
        if (recent.every((s) => s === 'EVEN')) side = 'EVEN';
        else if (recent.every((s) => s === 'ODD')) side = 'ODD';
      }
    } else if (ctx.modality === 'RISE_FALL') {
      const recent = ruleWindow(ctx.modality, ctx.window).sides.slice(0, n);
      if (recent.length === n) {
        if (recent.every((s) => s === 'RISE')) side = 'RISE';
        else if (recent.every((s) => s === 'FALL')) side = 'FALL';
      }
    }
    if (!side) return { intent: null, state: ctx.state, reason: `Aguardando sequência de ${n}` };
    const direction = ctx.params.entry === 'WITH' ? side : opposite(ctx.modality, side);
    return { intent: { direction }, state: ctx.state, reason: `Sequência de ${n} (${side}) → ${direction}` };
  },
  onSettlement: (ctx) => ctx.state,
};

// ---------------------------------------------------------------------------

/**
 * What a session of the Automação Invexa runs: a copy of its game's robots (Admin › Estratégias) taken
 * when it starts, so editing them never changes a running session. `{}` = sessions before 2026-10-26:
 * the built-in robots.
 */
export const autoParamsSchema = z.union([
  z.object({ v: z.literal(1).default(1), robots: z.array(comboRobotSchema).min(1).max(PRESET_LIMITS.perGame) }).strict(),
  z.object({}).strict(),
]);
export type AutoParams = z.infer<typeof autoParamsSchema>;

/** The Automação Invexa's params for a game, from a list of ready-made robots. */
export function autoParams(list: readonly InvexaPreset[], modality: Modality): AutoParams {
  return { v: 1, robots: autoPresets(list, modality).map((p) => ({ id: p.key, name: p.name, def: p.def })) };
}

function autoCombo(params: unknown, modality: Modality): ComboParams {
  const robots = (params as { robots?: ComboParams['robots'] } | null)?.robots;
  // One robot runs as a combination of one, many as a combination of many: same state and status line.
  return { v: 1, robots: robots?.length ? robots : (autoParams(DEFAULT_INVEXA_PRESETS, modality) as { robots: ComboParams['robots'] }).robots } as ComboParams;
}

/** State of a session started with the old patterns ({ last, trigger }): the running chain keeps its side. */
function autoState(raw: unknown, combo: ComboParams, modality: Modality): unknown {
  const st = raw as { subs?: unknown; last?: Direction | null } | null;
  if (!st || st.subs || !st.last) return raw;
  const init = comboStrategy.initialState(combo, modality);
  return { ...init, subs: init.subs.map((s, i) => (i === 0 ? { ...s, last: st.last ?? null } : s)), owner: 0 };
}

/**
 * The platform's built-in automation. The user only sets money and limits; it runs every strategy on
 * the game's list in Admin › Estratégias as one session, like "Robôs combinados": the first trigger (list
 * order) takes the cycle and its robot decides the gales. Owner's decision 2026-10-25: the old internal patterns
 * (break, alternation, doubles, streak of 8) lost cycles every day; they were replaced by the
 * ready-made robots. Past results never promise future ones.
 */
export const autoPatterns: Strategy<AutoParams, ComboState> = {
  key: 'invexa_auto',
  name: 'Automação Invexa',
  description: 'Os robôs prontos do mercado numa só automação: entra no primeiro gatilho que aparecer.',
  modalities: ['EVEN_ODD', 'RISE_FALL'],
  paramsSchema: autoParamsSchema as unknown as z.ZodType<AutoParams>,
  defaultParams: () => ({}),
  validate: (params, modality) => {
    const robots = (params as { robots?: ComboParams['robots'] }).robots;
    if (!robots) return null;
    if (new Set(robots.map((r) => r.id)).size !== robots.length) return 'Estratégias repetidas na Automação Invexa.';
    for (const r of robots) {
      const error = validateCustomDef(r.def, modality);
      if (error) return `${r.name}: ${error}`;
    }
    return null;
  },
  initialState: (params, modality) => comboStrategy.initialState(autoCombo(params, modality), modality),
  onTick: (ctx, tick) => {
    const combo = autoCombo(ctx.params, ctx.modality);
    return comboStrategy.onTick({ ...ctx, params: combo, state: autoState(ctx.state, combo, ctx.modality) as ComboState }, tick);
  },
  onSettlement: (ctx, settled) => {
    const combo = autoCombo(ctx.params, ctx.modality);
    return comboStrategy.onSettlement({ ...ctx, params: combo, state: autoState(ctx.state, combo, ctx.modality) as ComboState }, settled);
  },
  // A stop set on one of its robots ends the session (sessions before 2026-10-26 ran robots without stops).
  shouldEnd: (state, params) => {
    const robots = (params as { robots?: ComboParams['robots'] }).robots;
    return robots?.length ? (comboStrategy.shouldEnd?.(state, { v: 1, robots } as ComboParams) ?? null) : null;
  },
  statusNote: comboStrategy.statusNote,
};

// ---------------------------------------------------------------------------

/** Built-in ready-made robots (listing only: every `invexa_…` key resolves through presetStrategyFor). */
const presetStrategies = DEFAULT_INVEXA_PRESETS.map((p) => presetStrategyFor(p.key)!);

export const STRATEGIES: readonly AnyStrategy[] = [autoPatterns, ...presetStrategies, fixedDirection, alternating, switchAfterLoss, streakTrigger, customStrategy, comboStrategy, accumulatorBot];

/** Strategy used by the one-button automation page. */
export const AUTO_STRATEGY_KEY = autoPatterns.key;

/** A strategy by key: the fixed ones, or a ready-made robot (also those created in the admin). */
function findStrategy(key: string): AnyStrategy | null {
  return STRATEGIES.find((x) => x.key === key) ?? presetStrategyFor(key);
}

export function getStrategy(key: string): AnyStrategy {
  const s = findStrategy(key);
  if (!s) throw new Error(`Unknown strategy: ${key}`);
  return s;
}

/** Validate params + modality compatibility. Returns parsed params or an error message. */
export function parseStrategyParams(
  key: string,
  modality: Modality,
  raw: unknown,
): { ok: true; params: unknown } | { ok: false; error: string } {
  const strategy = findStrategy(key);
  if (!strategy) return { ok: false, error: 'Estratégia desconhecida.' };
  if (!strategy.modalities.includes(modality)) return { ok: false, error: 'Estratégia não disponível para esta modalidade.' };
  const parsed = strategy.paramsSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, error: 'Parâmetros da estratégia inválidos.' };
  const params = parsed.data as Record<string, unknown>;
  const extra = strategy.validate?.(params, modality);
  if (extra) return { ok: false, error: extra };
  const valid = new Set<string>(MODALITY_SPECS[modality].directions.map((d) => d.direction));
  for (const field of ['direction', 'startDirection']) {
    const v = params[field];
    if (typeof v === 'string' && !valid.has(v)) return { ok: false, error: 'Direção inválida para a modalidade.' };
  }
  const dir = (params.direction ?? params.startDirection) as Direction | undefined;
  if (dir) {
    // Strategies other than fixed_direction may use both directions, so the
    // barrier must be valid for every direction they can switch to.
    const used = MODALITY_SPECS[modality].directions.filter((d) => key !== 'fixed_direction' || d.direction === dir);
    for (const d of used) {
      if (!d.barrier) continue;
      const barrier = params.barrier;
      if (typeof barrier !== 'number' || barrier < d.barrier.min || barrier > d.barrier.max) {
        return { ok: false, error: `Dígito para "${d.label}" deve estar entre ${d.barrier.min} e ${d.barrier.max}.` };
      }
    }
  }
  return { ok: true, params };
}
export * from './rush-analysis';
